import {
    InspectionService,
    getQuestionMediaList,
    mergeQuestionFilesPreservingMeta,
    TemplateQuestionResponseType,
    TemplateResponseType,
} from 'utility'

export { mergeQuestionFilesPreservingMeta }

export const SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID = 129321

/** Customer Claim Non RMA — same portal inspection UI as 129321. */
export const SPECIAL_CUSTOMER_CLAIM_NON_RMA_TEMPLATE_ID = 170965

export const SPECIAL_CUSTOMER_CLAIM_TEMPLATE_IDS = [
    SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
    SPECIAL_CUSTOMER_CLAIM_NON_RMA_TEMPLATE_ID,
]

/** Legacy template GUID (still supported). */
export const SPECIAL_CUSTOMER_CLAIM_TEMPLATE_GUID_LEGACY =
    'ffbbc220-75f4-44d8-a4c8-99a438728806'

/** Current audit instance GUID from Customer Claim template. */
export const SPECIAL_CUSTOMER_CLAIM_AUDIT_TEMPLATE_GUID =
    'bd550d1d-98d1-4c2b-85e1-c670da308ff3'

export const isSpecialCustomerClaimTemplateId = templateId =>
    SPECIAL_CUSTOMER_CLAIM_TEMPLATE_IDS.includes(Number(templateId))

export const isCustomerClaimNonRmaTemplateId = templateId =>
    Number(templateId) === SPECIAL_CUSTOMER_CLAIM_NON_RMA_TEMPLATE_ID

export const CLAIM_LAYOUT_CODE = 'Claim'

export const readLayoutCode = source =>
    String(source?.LayoutCode ?? source?.layoutCode ?? '').trim()

export const isClaimLayoutCode = source =>
    readLayoutCode(source).toLowerCase() === CLAIM_LAYOUT_CODE.toLowerCase()

export const PORTAL_SECTION_NAME = 'Customer Service Claim Portal'
export const QUALITY_SECTION_NAME = 'Quality'
export const COMPLETION_SECTION_NAME = 'Customer Service Completion'

export const PORTAL_FAQ = {
    customer: ['customer', 'customer / supplier', 'customer/supplier'],
    matType: ['mat type'],
    itemSketch: ['item/sketch number', 'item sketch number'],
    size: ['size'],
    quantity: ['quantity'],
    sfoOrder: ['sfo/order', 'sfo/order #', 'sfo'],
    complaintDetails: [
        'customer complain details',
        'customer complaint details',
        '7. customer complain details',
        '7. customer complaint details',
    ],
    code: ['code'],
    rma: ['rma', 'rma#'],
    creditIssue: ['credit issue'],
    notes: ['notes'],
}

export const QUALITY_FAQ = {
    receivingDate: ['receiving date'],
    carrier: ['carrier'],
    tracking: ['tracking', 'tracking #'],
    emboss: ['emboss'],
    packingMatCondition: [
        'packing/mat condition',
        'packing / mat condition',
    ],
    disposition: ['disposition'],
    status: ['status'],
    notes: ['notes'],
}

export const COMPLETION_FAQ = {
    creditIssue: ['credit issue'],
    notes: ['notes'],
}

export const normalizeFaq = faq => (faq || '').trim().toLowerCase()

const getOptionLabel = opt =>
    String(opt?.DisplayText || opt?.Text || opt?.Response || '').trim()

const getOtherAnswerText = question => {
    const answers = question?.Answers || {}
    const list = Array.isArray(answers.OtherAnswerResponse)
        ? answers.OtherAnswerResponse
        : []
    const other = list.find(item => {
        if (!item) return false
        const value =
            typeof item.OtherAnswer === 'string'
                ? item.OtherAnswer.trim()
                : item.OtherAnswer
        return value !== undefined && value !== null && value !== ''
    })
    return other ? String(other.OtherAnswer).trim() : ''
}

const optionExactlyMatchesText = (opt, selectedNormalized) => {
    const label = getOptionLabel(opt).toLowerCase()
    const locationName = String(opt?.ExtensiveExternal?.LocationName || '')
        .trim()
        .toLowerCase()
    return (
        !!selectedNormalized &&
        (label === selectedNormalized ||
            (!!locationName && locationName === selectedNormalized))
    )
}

const getSelectedMultipleText = question => {
    const options = question?.Answers?.MultipleResponse || []
    const selected = options.find(opt => opt?.IsSelected)
    if (selected) return getOptionLabel(selected)
    return getOtherAnswerText(question)
}

const getSingleResponseText = question => {
    const response = question?.Answers?.SingleResponse?.Response
    if (response != null && String(response).trim() !== '') {
        return String(response).trim()
    }
    if (question?.Response != null && String(question.Response).trim() !== '') {
        return String(question.Response).trim()
    }
    return ''
}

export const questionAllowsOtherOption = question =>
    !!question?.IsOtherOptionAvailable

const isClaimCustomerQuestion = question => {
    const faq = normalizeFaq(question?.FAQ || question?.Question)
    return PORTAL_FAQ.customer.includes(faq)
}

/**
 * Reuse the latest saved Other row so autosave updates it.
 * A new object without UniqueId is inserted again on every save.
 */
const buildSingleOtherAnswer = (question, trimmed) => {
    const list = Array.isArray(question?.Answers?.OtherAnswerResponse)
        ? question.Answers.OtherAnswerResponse
        : []
    const normalized = String(trimmed || '').trim().toLowerCase()
    const matching = [...list].reverse().find(item => {
        if (!item) return false
        return String(item.OtherAnswer || '').trim().toLowerCase() === normalized
    })
    const source =
        matching ||
        [...list].reverse().find(item => item && item.UniqueId)
    if (source) {
        return [
            {
                ...source,
                OtherAnswer: trimmed,
                QuestionId: question?.Id ?? source.QuestionId,
            },
        ]
    }
    return [
        {
            OtherAnswer: trimmed,
            QuestionId: question?.Id,
            IsScore: false,
            Score: '',
        },
    ]
}

/**
 * Phase 1 complete only accepts a selected customer option.
 * A free-text Other answer is stored, then ignored (IsAnswered stays false).
 */
const buildAddedCustomerOption = (question, trimmed) => {
    const sample = (question?.Answers?.MultipleResponse || []).find(Boolean) || {}
    return {
        Id: 0,
        ResponseId: 0,
        DisplayText: trimmed,
        Text: trimmed,
        Response: trimmed,
        IsSelected: true,
        QuestionId: question?.Id ?? sample.QuestionId,
        QuestionResponseId:
            question?.QuestionResponseId ?? sample.QuestionResponseId,
        GroupId: sample.GroupId ?? question?.QuestionResponseId,
        DisplayOrder: Number(sample.DisplayOrder || 0) + 1,
        Score: 0,
        IsScore: false,
        MultiResponseType:
            question?.MultiResponseType ?? sample.MultiResponseType,
        ExtensiveExternal: {
            LocationName: trimmed,
        },
    }
}

const withAnsweredFlag = (question, answers, answered) => ({
    ...question,
    Answers: {
        ...answers,
        IsAnswered: answered,
    },
    IsOtherAnswerSelected: !!answers?.IsOtherAnswerSelected,
    IsAnswered: answered,
})

export const isClaimMultipleChoiceQuestion = question => {
    if (!question) return false
    if ((question.Answers?.MultipleResponse || []).length > 0) return true
    if (question.TypeOfResponse === TemplateResponseType.Multiple) return true
    if (
        question.MultiResponseType === TemplateQuestionResponseType.Single ||
        question.MultiResponseType === TemplateQuestionResponseType.Multiple
    ) {
        return true
    }
    return false
}

export const setMultipleSelectionByText = (question, selectedText) => {
    const trimmed = String(selectedText || '').trim()
    const selectedNormalized = trimmed.toLowerCase()
    const multi = question?.Answers?.MultipleResponse || []
    const allowOther = questionAllowsOtherOption(question)
    if (!multi.length) {
        if (allowOther && isClaimCustomerQuestion(question) && trimmed) {
            return withAnsweredFlag(
                question,
                {
                    ...(question?.Answers || {}),
                    MultipleResponse: [buildAddedCustomerOption(question, trimmed)],
                    IsOtherAnswerSelected: true,
                    OtherAnswerResponse: buildSingleOtherAnswer(question, trimmed),
                },
                true,
            )
        }
        if (allowOther) {
            const useOther = !!trimmed
            return withAnsweredFlag(
                question,
                {
                    ...(question?.Answers || {}),
                    MultipleResponse: [],
                    IsOtherAnswerSelected: useOther,
                    OtherAnswerResponse: useOther
                        ? buildSingleOtherAnswer(question, trimmed)
                        : null,
                },
                useOther,
            )
        }
        return setSingleResponseText(question, selectedText)
    }
    const options = multi.map(opt => {
        if (!selectedNormalized) {
            return { ...opt, IsSelected: false }
        }
        if (allowOther) {
            return {
                ...opt,
                IsSelected: optionExactlyMatchesText(opt, selectedNormalized),
            }
        }
        const label = getOptionLabel(opt).toLowerCase()
        const locationName = String(
            opt?.ExtensiveExternal?.LocationName || '',
        )
            .trim()
            .toLowerCase()
        const isSelected =
            label === selectedNormalized ||
            locationName === selectedNormalized ||
            label.includes(selectedNormalized) ||
            selectedNormalized.includes(label) ||
            (locationName &&
                (locationName.includes(selectedNormalized) ||
                    selectedNormalized.includes(locationName)))
        return { ...opt, IsSelected: isSelected }
    })
    let nextOptions = options
    let hasSelected = nextOptions.some(opt => !!opt.IsSelected)
    const addCustomCustomer =
        allowOther &&
        isClaimCustomerQuestion(question) &&
        !!trimmed &&
        !hasSelected
    if (addCustomCustomer) {
        nextOptions = [
            ...nextOptions,
            buildAddedCustomerOption(question, trimmed),
        ]
        hasSelected = true
    }
    const useOther =
        (allowOther && !!trimmed && !hasSelected) || addCustomCustomer
    const answered = hasSelected || useOther
    return withAnsweredFlag(
        question,
        {
            ...(question?.Answers || {}),
            MultipleResponse: nextOptions,
            IsOtherAnswerSelected: useOther,
            OtherAnswerResponse: useOther
                ? buildSingleOtherAnswer(question, trimmed)
                : null,
        },
        answered,
    )
}

export const setSingleResponseText = (question, textValue) => {
    const value = String(textValue ?? '')
    return {
        ...question,
        Answers: {
            ...(question?.Answers || {}),
            SingleResponse: {
                ...(question?.Answers?.SingleResponse || {}),
                Response: value,
                IsSelected: value.trim() !== '',
            },
        },
        IsAnswered: value.trim() !== '',
    }
}

export const buildQuestionMapByFaq = questions => {
    const map = {}
    const list = questions || []
    list.forEach(q => {
        const faqKey = normalizeFaq(q?.FAQ)
        if (faqKey && !map[faqKey]) {
            map[faqKey] = q
        }
        const questionKey = normalizeFaq(q?.Question)
        if (questionKey && !map[questionKey]) {
            map[questionKey] = q
        }
    })
    return map
}

/**
 * Resolve a template question by FAQ alias.
 * Single-word aliases (e.g. "customer") use exact FAQ only so they never
 * collide with "customer complain details".
 */
export const findQuestionByAliases = (questionMap, aliases = []) => {
    const keys = (aliases || []).map(normalizeFaq).filter(Boolean)
    for (let i = 0; i < keys.length; i += 1) {
        if (questionMap[keys[i]]) {
            return questionMap[keys[i]]
        }
    }
    const multiWordKeys = keys.filter(key => key.includes(' '))
    if (!multiWordKeys.length) {
        return null
    }
    const entries = Object.entries(questionMap || {})
    for (let i = 0; i < multiWordKeys.length; i += 1) {
        const key = multiWordKeys[i]
        const partial = entries.find(
            ([faq]) => faq === key || faq.startsWith(`${key} `),
        )
        if (partial) return partial[1]
    }
    return null
}

const mergeSectionQuestionsByBindings = (section, bindings) => {
    if (!section?.Questions?.length) return section
    const map = buildQuestionMapByFaq(section.Questions)
    const valueByQuestionId = new Map()
    bindings.forEach(({ aliases, value, isDate }) => {
        const matched = findQuestionByAliases(map, aliases)
        if (matched?.Id == null) return
        const raw = value ?? ''
        const applied = isDate
            ? setSingleResponseText(matched, formatDateForApi(raw) || '')
            : applyValueToQuestion(matched, raw)
        valueByQuestionId.set(matched.Id, applied)
    })
    const questions = section.Questions.map(q => {
        const applied = valueByQuestionId.get(q.Id)
        if (!applied) return q
        return {
            ...applied,
            SectionId: section.SectionId,
            SectionName: section.SectionName,
        }
    })
    return { ...section, Questions: questions }
}

export const findSectionByName = (sections, name) => {
    const target = normalizeFaq(name)
    return (sections || []).find(
        s => normalizeFaq(s?.SectionName) === target,
    )
}

export const getPortalTemplateSection = sections =>
    listClaimPortalSectionsFromAudit(sections)[0] || null

/** True when audit/template sections include the claim portal layout. */
export const hasCustomerClaimPortalLayout = sections =>
    Boolean(getPortalTemplateSection(sections))

/**
 * True when a section is the Customer Service Claim Portal block.
 * Mat Type alone is not enough (WK templates like Tuft Lock also have Mat Type).
 */
export const isClaimPortalSection = section => {
    const map = buildQuestionMapByFaq(section?.Questions)
    if (
        normalizeFaq(section?.SectionName) === normalizeFaq(PORTAL_SECTION_NAME)
    ) {
        return true
    }
    const hasCustomer = !!findQuestionByAliases(map, PORTAL_FAQ.customer)
    if (!hasCustomer) {
        return false
    }
    return (
        !!findQuestionByAliases(map, PORTAL_FAQ.complaintDetails) ||
        !!findQuestionByAliases(map, PORTAL_FAQ.itemSketch) ||
        !!findQuestionByAliases(map, PORTAL_FAQ.sfoOrder)
    )
}

/**
 * Claim inspection page only. Special portal UI follows LayoutCode, not template id.
 * Other screens still use the template-id helpers above.
 */
export const isSpecialCustomerClaimTemplate = (audit, companyId) =>
    Number(companyId) === 34930 && isClaimLayoutCode(audit)

const sectionMatchesTemplateQuestion = (section, templateQuestion) => {
    const templateSectionId = String(templateQuestion?.SectionId ?? '').trim()
    const templateSectionName = normalizeFaq(templateQuestion?.SectionName)
    if (templateSectionId) {
        return (
            String(section?.SectionId ?? '') === templateSectionId ||
            String(section?.Id ?? '') === templateSectionId
        )
    }
    if (templateSectionName) {
        return normalizeFaq(section?.SectionName) === templateSectionName
    }
    return false
}

/** Map a template question to the live inspection question (for Ids / media). */
export const findInspectionQuestionByTemplateRef = (
    inspectionSections,
    templateQuestion,
) => {
    if (!templateQuestion || !Array.isArray(inspectionSections)) {
        return null
    }
    const targetOid = String(templateQuestion?.QuestionOriginalId || '')
        .trim()
        .toLowerCase()
    const targetFaq = normalizeFaq(
        templateQuestion?.FAQ || templateQuestion?.Question,
    )
    const hasSectionScope =
        Boolean(String(templateQuestion?.SectionId ?? '').trim()) ||
        Boolean(normalizeFaq(templateQuestion?.SectionName))

    const scopedSections = hasSectionScope
        ? inspectionSections.filter(section =>
              sectionMatchesTemplateQuestion(section, templateQuestion),
          )
        : []
    const sectionsToSearch =
        scopedSections.length > 0 ? scopedSections : inspectionSections

    for (let s = 0; s < sectionsToSearch.length; s += 1) {
        const questions = sectionsToSearch[s]?.Questions || []
        for (let i = 0; i < questions.length; i += 1) {
            const q = questions[i]
            const oid = String(q?.QuestionOriginalId || '')
                .trim()
                .toLowerCase()
            if (targetOid && oid && oid === targetOid) {
                return q
            }
            if (targetFaq && normalizeFaq(q?.FAQ) === targetFaq) {
                return q
            }
        }
    }
    return null
}

export const listClaimPortalSectionsFromAudit = sections => {
    const list = (sections || []).filter(isClaimPortalSection)
    return list.sort((a, b) => {
        const oa = Number(a?.Order ?? a?.SectionOrder ?? 0)
        const ob = Number(b?.Order ?? b?.SectionOrder ?? 0)
        if (oa !== ob) return oa - ob
        return String(a?.SectionId || '').localeCompare(String(b?.SectionId || ''))
    })
}

/**
 * Resolve dropdown/checkbox options from template root TemplateResponseType.
 * Matches standard template preview: find group by QuestionResponseId, then all options in group.
 */
const resolveTemplateMultipleResponseOptions = (question, templateData) => {
    if (!question || !templateData?.TemplateResponseType) {
        return []
    }
    const responseType = templateData.TemplateResponseType
    const standardOpts = responseType.MultipleResponse || []
    const userOpts = responseType.UserMultipleResponse || []
    const questionResponseId = question?.QuestionResponseId
    const questionId = question?.Id

    const findSeed = opts =>
        (opts || []).find(
            opt =>
                opt.Id === questionResponseId ||
                opt.GroupId === questionResponseId ||
                (questionResponseId === 0 &&
                    (opt.GroupId === questionId || opt.Id === questionId)),
        )

    const seed = findSeed(userOpts) || findSeed(standardOpts)
    if (seed) {
        const groupId = seed.GroupId ?? seed.Id
        const userGroup = userOpts.filter(opt => opt.GroupId === groupId)
        if (userGroup.length) {
            return userGroup
        }
        return standardOpts.filter(opt => opt.GroupId === groupId)
    }

    const optionMatcher = opt =>
        opt.GroupId === questionResponseId ||
        opt.Id === questionResponseId ||
        (questionResponseId === 0 &&
            (opt.GroupId === questionId || opt.Id === questionId))
    const userMatched = userOpts.filter(optionMatcher)
    if (userMatched.length) {
        return userMatched
    }
    return standardOpts.filter(optionMatcher)
}

/**
 * Template preview API returns options on TemplateResponseType, not Answers.MultipleResponse.
 * Hydrate questions so claim portal dropdowns match the standard InspectionSectionList preview.
 */
export const hydrateTemplatePreviewSections = (sections, templateData) => {
    const data = templateData || {}
    return (Array.isArray(sections) ? sections : []).map(section => ({
        ...section,
        Questions: Array.isArray(section?.Questions)
            ? section.Questions.map(q => {
                const ans = { ...(q.Answers || {}) }

                if (!Array.isArray(ans.MultipleResponse) || !ans.MultipleResponse.length) {
                    const baseOptions = resolveTemplateMultipleResponseOptions(q, data)
                    ans.MultipleResponse = baseOptions.map(opt => ({
                        ...opt,
                        IsSelected: false,
                    }))
                }

                const inferredMultiResponseType = (() => {
                    if (
                        q.MultiResponseType ===
                            TemplateQuestionResponseType.Single ||
                        q.MultiResponseType ===
                            TemplateQuestionResponseType.Multiple
                    ) {
                        return q.MultiResponseType
                    }

                    if (
                        (q.TypeOfResponse ?? q.QuestionType) ===
                        TemplateResponseType.Multiple
                    ) {
                        const selectedOptionType = ans.MultipleResponse?.find(
                            opt =>
                                opt?.MultiResponseType ===
                                    TemplateQuestionResponseType.Single ||
                                opt?.MultiResponseType ===
                                    TemplateQuestionResponseType.Multiple,
                        )?.MultiResponseType

                        return (
                            selectedOptionType ||
                            TemplateQuestionResponseType.Single
                        )
                    }

                    return 0
                })()

                if (!ans.SingleResponse) {
                    ans.SingleResponse = { Response: '' }
                }

                if (
                    !ans.ParentChildResponse &&
                    Array.isArray(q.ParentChildResponse)
                ) {
                    ans.ParentChildResponse = JSON.parse(
                        JSON.stringify(q.ParentChildResponse),
                    ).map(parent => ({
                        ...parent,
                        Id: parent.Id || parent.ParentId,
                        IsSelected: false,
                        Children: Array.isArray(parent.Children)
                            ? parent.Children.map(c => ({
                                ...c,
                                Id: c.Id || c.ChildId,
                                ParentId:
                                    c.ParentId || parent.ParentId || parent.Id,
                                IsScore:
                                    c.IsScore !== undefined
                                        ? c.IsScore
                                        : !!c.IsChildScore,
                                Score:
                                    c.Score !== undefined
                                        ? c.Score
                                        : c.ChildScore || 0,
                                IsSelected: false,
                            }))
                            : [],
                    }))
                }

                return {
                    ...q,
                    FAQ: q.FAQ || q.Question,
                    TypeOfResponse:
                        typeof q.TypeOfResponse !== 'undefined'
                            ? q.TypeOfResponse
                            : q.QuestionType,
                    MultiResponseType: inferredMultiResponseType,
                    DisplayOrder:
                        typeof q.DisplayOrder !== 'undefined'
                            ? q.DisplayOrder
                            : q.Order || q.QuestionOrder,
                    QuestionReferences:
                        q.QuestionReferences || q.QuestionReference || [],
                    Answers: ans,
                }
            })
            : section.Questions,
        SectionURL:
            section?.SectionURL ||
            section?.SectionUrl ||
            section?.Questions?.[0]?.SectionURL ||
            section?.Questions?.[0]?.SectionUrl ||
            null,
    }))
}

/** Hydrate template sections and split list for claim vs standard preview. */
export const buildTemplatePreviewSections = (
    sections,
    templateData,
    isClaimTemplate,
) => {
    const hydratedSections = hydrateTemplatePreviewSections(sections, templateData)
    if (!isClaimTemplate) {
        return { hydratedSections, listSections: hydratedSections }
    }
    const portalKey = PORTAL_SECTION_NAME.trim().toLowerCase()
    const qualityKey = QUALITY_SECTION_NAME.trim().toLowerCase()
    const completionKey = COMPLETION_SECTION_NAME.trim().toLowerCase()
    const listSections = hydratedSections.filter(section => {
        const name = normalizeFaq(section?.SectionName)
        if (name === portalKey) return false
        if (name === qualityKey) return false
        if (name === completionKey) return false
        return true
    })
    return { hydratedSections, listSections }
}

const multipleResponseIdentity = opt => {
    const rawId = opt?.ResponseId ?? opt?.Id
    const id =
        rawId != null && String(rawId).trim() !== '' ? String(rawId) : ''
    return { id, label: getOptionLabel(opt).toLowerCase() }
}

/**
 * Audit questions often store only the selected choice. Template response
 * options added later (Complete, Final, and the rest) still have to appear.
 */
const mergeTemplateOptionsIntoAnswers = (fromAnswers, fromTemplate) => {
    if (!fromTemplate?.length) return fromAnswers || []
    if (!fromAnswers?.length) return fromTemplate

    const seenIds = new Set()
    const seenLabels = new Set()
    fromAnswers.forEach(opt => {
        const { id, label } = multipleResponseIdentity(opt)
        if (id) seenIds.add(id)
        if (label) seenLabels.add(label)
    })

    const extras = fromTemplate
        .filter(opt => {
            const { id, label } = multipleResponseIdentity(opt)
            if (id && seenIds.has(id)) return false
            if (label && seenLabels.has(label)) return false
            return Boolean(id || label)
        })
        .map(opt => ({ ...opt, IsSelected: false }))

    return extras.length ? [...fromAnswers, ...extras] : fromAnswers
}

const resolveQuestionMultipleResponse = (question, templateData) => {
    const fromAnswers = question?.Answers?.MultipleResponse || []
    if (!question) {
        return fromAnswers
    }
    const fromTemplate = resolveTemplateMultipleResponseOptions(
        question,
        templateData,
    )
    return mergeTemplateOptionsIntoAnswers(fromAnswers, fromTemplate)
}

export const questionToDropdownOptions = (question, templateData) => {
    const multi = resolveQuestionMultipleResponse(question, templateData)
    const options = (multi || [])
        .slice()
        .sort(
            (a, b) =>
                Number(a?.DisplayOrder ?? 0) - Number(b?.DisplayOrder ?? 0),
        )
        .map(opt => {
            const label = getOptionLabel(opt)
            return {
                key: String(opt?.ResponseId || opt?.Id || label),
                value: label,
                text: label,
            }
        })
        .filter(opt => opt.value)

    const otherText = getOtherAnswerText(question)
    if (otherText && !options.some(opt => String(opt.value).trim() === otherText)) {
        options.push({
            key: `other-${otherText}`,
            value: otherText,
            text: otherText,
        })
    }
    return options
}

export const questionUsesMultipleResponse = question =>
    isClaimMultipleChoiceQuestion(question)

export const applyValueToQuestion = (question, value) => {
    if (isClaimMultipleChoiceQuestion(question)) {
        return setMultipleSelectionByText(question, value)
    }
    return setSingleResponseText(question, value)
}

/** Current answer text for any template question (dropdown or typed). */
export const readQuestionDisplayValue = question => {
    if (!question) return ''
    if (isClaimMultipleChoiceQuestion(question)) {
        return (
            getSelectedMultipleText(question) || getSingleResponseText(question)
        )
    }
    return getSingleResponseText(question)
}

export const claimExtraAnswerKey = (sectionId, question) => {
    const faq = normalizeFaq(question?.FAQ || question?.Question)
    return `${String(sectionId ?? '')}::${String(question?.Id ?? '')}::${faq}`
}

export const sortAuditSectionsForClaim = sections =>
    (sections || [])
        .map((section, index) => ({ section, index }))
        .sort((a, b) => {
            const orderA = Number(a.section?.Order ?? a.section?.SectionOrder ?? a.index)
            const orderB = Number(b.section?.Order ?? b.section?.SectionOrder ?? b.index)
            if (orderA !== orderB) return orderA - orderB
            return a.index - b.index
        })
        .map(({ section }) => section)

const claimSectionId = section => {
    if (section?.SectionId == null || String(section.SectionId).trim() === '') {
        return ''
    }
    return String(section.SectionId)
}

/**
 * Non RMA loads phases from Customer Claim (129321), whose section ids do not
 * match the live audit. Known sections keep their phase. Any other live
 * section takes the next unmatched phase from the end, so a renamed or newly
 * added section still gets its Complete button. Quality is dropped when that
 * section is not on the form.
 */
export const buildClaimLiveSectionIdsByPhase = (statuses, sections) => {
    const ordered = sortAuditSectionsForClaim(sections)
    const byPhase = {}
    const used = new Set()

    const take = (phaseOrder, section) => {
        const id = claimSectionId(section)
        if (!id || used.has(id)) return false
        used.add(id)
        byPhase[phaseOrder] = id
        return true
    }

    const phases = []
    ;(statuses || []).forEach(entry => {
        const phase = Number(entry?.phaseOrder ?? entry?.PhaseOrder)
        if (phase > 0 && !phases.includes(phase)) phases.push(phase)
    })
    phases.sort((a, b) => a - b)

    const portal =
        findSectionByName(ordered, PORTAL_SECTION_NAME) ||
        ordered.find(section => isClaimPortalSection(section))
    const quality = findSectionByName(ordered, QUALITY_SECTION_NAME)
    const completion = findSectionByName(ordered, COMPLETION_SECTION_NAME)

    if (phases.includes(1)) take(1, portal)
    if (phases.includes(2)) {
        if (!take(2, quality)) byPhase[2] = undefined
    }
    if (phases.includes(3)) take(3, completion)

    const unmatchedSections = ordered.filter(section => {
        const id = claimSectionId(section)
        return id && !used.has(id)
    })
    const openPhases = phases.filter(
        phase => !Object.prototype.hasOwnProperty.call(byPhase, phase),
    )
    const pairCount = Math.min(unmatchedSections.length, openPhases.length)
    for (let i = 0; i < pairCount; i += 1) {
        take(
            openPhases[openPhases.length - 1 - i],
            unmatchedSections[unmatchedSections.length - 1 - i],
        )
    }
    openPhases.forEach(phase => {
        if (!Object.prototype.hasOwnProperty.call(byPhase, phase)) {
            byPhase[phase] = undefined
        }
    })
    return byPhase
}

/**
 * Questions on a section that the fixed claim fields do not already render.
 * Keeps template order so a newly added question shows up in that section.
 */
export const listUnmatchedSectionQuestions = (section, matchedQuestions) => {
    const matched = new Set(
        (matchedQuestions || [])
            .filter(question => question?.Id != null && String(question.Id) !== '')
            .map(question => String(question.Id)),
    )
    return (section?.Questions || [])
        .map((question, index) => ({ question, index }))
        .filter(({ question }) => question && !matched.has(String(question.Id)))
        .sort((a, b) => {
            const rawA = a.question?.Order ?? a.question?.DisplayOrder
            const rawB = b.question?.Order ?? b.question?.DisplayOrder
            const orderA =
                rawA == null || rawA === '' ? a.index : Number(rawA)
            const orderB =
                rawB == null || rawB === '' ? b.index : Number(rawB)
            if (orderA !== orderB) return orderA - orderB
            return a.index - b.index
        })
        .map(({ question }) => question)
}

export const applyExtraAnswersToSectionQuestions = (section, extraAnswers) => {
    if (!section || !extraAnswers || typeof extraAnswers !== 'object') {
        return section
    }
    let changed = false
    const questions = (section.Questions || []).map(question => {
        const key = claimExtraAnswerKey(section.SectionId, question)
        if (!Object.prototype.hasOwnProperty.call(extraAnswers, key)) {
            return question
        }
        changed = true
        return applyValueToQuestion(question, extraAnswers[key] ?? '')
    })
    if (!changed) return section
    return { ...section, Questions: questions }
}

/**
 * Update only one question's Files in audit sections (after media add/remove).
 * Full `setData({ Sections })` re-hydrates the claim portal and clears unsaved fields.
 */
export const mergeQuestionFilesFromUpdatedSections = (
    prevSections,
    updatedSections,
    questionId,
) => {
    const qid = String(questionId ?? '')
    if (!qid) return prevSections || []

    const prevQuestion = (prevSections || [])
        .flatMap(section => section.Questions || [])
        .find(q => String(q?.Id ?? '') === qid)
    const matchedQuestion = (updatedSections || [])
        .flatMap(section => section.Questions || [])
        .find(q => String(q?.Id ?? '') === qid)

    if (!matchedQuestion) return prevSections || []

    const mergedFiles = mergeQuestionFilesPreservingMeta(
        getQuestionMediaList(prevQuestion || {}),
        getQuestionMediaList(matchedQuestion || {}),
    )

    return (prevSections || []).map(section => ({
        ...section,
        Questions: (section.Questions || []).map(q => {
            if (String(q?.Id ?? '') !== qid) return q
            const next = { ...q, Files: mergedFiles }
            next.Answers = {
                ...(next.Answers || {}),
                MediaResponse: mergedFiles,
            }
            return next
        }),
    }))
}

/** Patch Files array for one question (caption/comment optimistic update). */
export const patchQuestionFilesInSections = (
    sections,
    questionId,
    patchFiles,
) => {
    const qid = String(questionId ?? '')
    if (!qid || typeof patchFiles !== 'function') return sections || []

    return (sections || []).map(section => ({
        ...section,
        Questions: (section.Questions || []).map(q => {
            if (String(q?.Id ?? '') !== qid) return q
            const nextFiles = patchFiles(q.Files || [])
            const next = { ...q, Files: nextFiles }
            if (next.Answers && Array.isArray(next.Answers.MediaResponse)) {
                next.Answers = {
                    ...next.Answers,
                    MediaResponse: nextFiles,
                }
            }
            return next
        }),
    }))
}

/** Keep local draft values when server sync returns empty strings (autosave). */
export const mergePortalDraftPreservingLocal = (serverDraft, localDraft) => {
    const server = serverDraft || {}
    const local = localDraft || {}
    const merged = { ...server }
    Object.keys(merged).forEach(key => {
        const serverVal = String(merged[key] ?? '').trim()
        const localVal = String(local[key] ?? '').trim()
        if (!serverVal && localVal) {
            merged[key] = local[key]
        }
    })
    return merged
}

/** Portal draft fields (excludes supplier — stored separately). */
export const portalRowToDraft = row => ({
    matType: row?.matType || '',
    itemSketchNumber: row?.itemSketchNumber || '',
    size: row?.size || '',
    quantity: row?.quantity || '',
    sfoOrder: row?.sfoOrder || '',
    complaintDetails: row?.complaintDetails || '',
    code: row?.code || '',
    rma: row?.rma || '',
    creditIssue: row?.creditIssue || '',
    notes: row?.notes || '',
})

/** Pull city / state / zip from a comma-separated formatted address. */
export const parseCityStateZipFromFormattedAddress = formatted => {
    const raw = String(formatted || '').trim()
    if (!raw) {
        return { city: '', stateProvince: '', zip: '', street: '' }
    }
    const parts = raw.split(',').map(s => s.trim()).filter(Boolean)
    if (parts.length < 3) {
        return { city: '', stateProvince: '', zip: '', street: raw }
    }
    const zip = parts[parts.length - 1] || ''
    const stateProvince = parts[parts.length - 2] || ''
    const city = parts[parts.length - 3] || ''
    const street = parts.slice(0, -3).join(', ')
    return { city, stateProvince, zip, street }
}

/** "Cincinnati, OH 45213-2516" for dropdown trigger / menu row (city, state, zip). */
export const formatSupplierCityStateZip = ({
    city = '',
    stateProvince = '',
    zip = '',
} = {}) => {
    const c = String(city || '').trim()
    const s = String(stateProvince || '').trim()
    const z = String(zip || '').trim()
    const cityState = c && s ? `${c}, ${s}` : c || s
    return [cityState, z].filter(Boolean).join(' ')
}

/** Parse supplier option fields from API `MultipleResponse` + `ExtensiveExternal`. */
export const parseSupplierOptionMeta = opt => {
    let ext = opt?.ExtensiveExternal || {}
    if (typeof ext === 'string') {
        try {
            ext = JSON.parse(ext)
        } catch {
            ext = {}
        }
    }
    const clientType = String(
        ext?.ClientType || opt?.ClientType || '',
    )
        .trim()
        .toUpperCase()
    const clientTypeName = String(
        opt?.ClientTypeName || ext?.ClientTypeName || '',
    ).trim()
    const locationName = String(
        ext?.LocationName || opt?.DisplayText || getOptionLabel(opt),
    ).trim()
    let city = String(ext?.City || opt?.City || '').trim()
    let stateProvince = String(
        ext?.StateProvince || ext?.State || opt?.StateProvince || opt?.State || '',
    ).trim()
    let zip = String(
        ext?.Zip || ext?.PostalCode || opt?.Zip || opt?.PostalCode || '',
    ).trim()
    let street = String(
        ext?.Address ||
            ext?.Street ||
            ext?.StreetAddress ||
            ext?.AddressLine1 ||
            ext?.MailingAddress ||
            '',
    ).trim()
    const cityStateZip = formatSupplierCityStateZip({
        city,
        stateProvince,
        zip,
    })
    let formattedAddress = String(
        ext?.FormattedAddress ||
            ext?.FullAddress ||
            ext?.CustomerFullAddress ||
            '',
    ).trim()

    if (!city && !stateProvince && !zip && formattedAddress) {
        const parsed = parseCityStateZipFromFormattedAddress(formattedAddress)
        if (!city) {
            city = parsed.city
        }
        if (!stateProvince) {
            stateProvince = parsed.stateProvince
        }
        if (!zip) {
            zip = parsed.zip
        }
        if (!street) {
            street = parsed.street
        }
    }

    const cityStateZipFinal = formatSupplierCityStateZip({
        city,
        stateProvince,
        zip,
    })

    if (!formattedAddress) {
        const parts = [street, city, stateProvince, zip].filter(Boolean)
        if (parts.length) {
            formattedAddress = parts.join(', ')
        }
    }
    const badge = getSupplierBadgeFromClientType(clientTypeName, clientType)
    return {
        clientType,
        clientTypeName,
        clientTypeCode: badge.code,
        locationName,
        city,
        stateProvince,
        zip,
        cityStateZip: cityStateZipFinal || cityStateZip,
        street,
        formattedAddress,
        value: getOptionLabel(opt) || locationName,
        extensiveExternal: ext,
    }
}

export const CUSTOMER_TYPE_RADIO_OPTIONS = [
    { value: 'C', label: 'Cintas' },
    { value: 'I', label: 'Guardian International' },
    { value: 'L', label: 'Guardian' },
]

export const getSupplierBadgeFromClientType = (
    clientTypeName,
    clientTypeCode = '',
) => {
    const code = String(clientTypeCode || '')
        .trim()
        .toUpperCase()
    if (code === 'C') {
        return { code: 'CINTAS', variant: 'cintas' }
    }
    if (code === 'I') {
        return { code: 'INTL', variant: 'international' }
    }
    if (code === 'L') {
        return { code: 'GUARD', variant: 'guardian' }
    }

    const normalized = String(clientTypeName || '').trim().toLowerCase()
    if (normalized.includes('cintas')) {
        return { code: 'CINTAS', variant: 'cintas' }
    }
    if (
        normalized.includes('international') ||
        normalized.includes('intl')
    ) {
        return { code: 'INTL', variant: 'international' }
    }
    if (normalized.includes('guardian')) {
        return { code: 'GUARD', variant: 'guardian' }
    }
    return { code: 'OUR', variant: 'our' }
}

const SUPPLIER_GROUP_ORDER = {
    cintas: 0,
    international: 1,
    guardian: 2,
    our: 3,
}

const getSupplierGroupHeaderLabel = (variant, count) => {
    if (variant === 'cintas') return `CINTAS — ${count}`
    if (variant === 'international') return `INTERNATIONAL — ${count}`
    if (variant === 'guardian') return `GUARDIAN — ${count}`
    return `OUR CUSTOMERS — ${count}`
}

export const getClientTypeFromOptionMeta = meta => {
    if (!meta) return ''
    const direct = String(meta.clientType || meta.extensiveExternal?.ClientType || '')
        .trim()
        .toUpperCase()
    if (direct) return direct
    const badge = getSupplierBadgeFromClientType(meta.clientTypeName)
    if (badge.variant === 'cintas') return 'C'
    if (badge.variant === 'international') return 'I'
    if (badge.variant === 'guardian') return 'L'
    return ''
}

const hasSupplierClientTypeMeta = options =>
    (options || []).some(
        opt =>
            !opt?.disabled &&
            opt?.className !== 'supplier-group-header' &&
            !!getClientTypeFromOptionMeta(opt?.meta),
    )

/** True when customer rows include C / I / L metadata for type radios. */
export const supplierOptionsHaveClientType = options =>
    hasSupplierClientTypeMeta(options)

/** Keep only supplier dropdown rows matching API `ClientType` (C / I / L). */
export const filterSupplierOptionsByClientType = (options, clientTypeCode) => {
    const code = String(clientTypeCode || '')
        .trim()
        .toUpperCase()
    if (!code) return options || []

    const list = options || []
    /* Non RMA (and similar) customer lists have no ClientType — show all rows. */
    if (!hasSupplierClientTypeMeta(list)) {
        return list
    }

    return list.filter(opt => {
        if (opt?.disabled || opt?.className === 'supplier-group-header') {
            return false
        }
        if (!opt?.meta) return false
        return getClientTypeFromOptionMeta(opt.meta) === code
    })
}

/** Build grouped supplier dropdown options from Customer question API data. */
export const buildSupplierDropdownOptionsFromQuestion = (question, templateData) => {
    const multi = resolveQuestionMultipleResponse(question, templateData)
    if (!multi.length) return []

    const hasExtensive = multi.some(
        opt =>
            opt?.ExtensiveExternal ||
            opt?.ClientTypeName ||
            opt?.ExtensiveExternal?.LocationName,
    )
    if (!hasExtensive) {
        return questionToDropdownOptions(question, templateData)
    }

    const items = multi
        .map(opt => ({
            opt,
            ...parseSupplierOptionMeta(opt),
        }))
        .sort(
            (a, b) =>
                Number(a.opt?.DisplayOrder ?? 0) -
                Number(b.opt?.DisplayOrder ?? 0),
        )

    const groups = new Map()
    items.forEach(item => {
        const badge = getSupplierBadgeFromClientType(
            item.clientTypeName,
            item.clientType,
        )
        const groupKey = badge.variant
        if (!groups.has(groupKey)) {
            groups.set(groupKey, { badge, items: [] })
        }
        groups.get(groupKey).items.push(item)
    })

    const sortedGroups = [...groups.entries()].sort(
        (a, b) =>
            (SUPPLIER_GROUP_ORDER[a[1].badge.variant] ?? 9) -
            (SUPPLIER_GROUP_ORDER[b[1].badge.variant] ?? 9),
    )

    const options = []
    sortedGroups.forEach(([, group]) => {
        const { badge, items: groupItems } = group
        options.push({
            key: `header-${badge.variant}`,
            value: `__header_${badge.variant}__`,
            text: getSupplierGroupHeaderLabel(badge.variant, groupItems.length),
            disabled: true,
            className: 'supplier-group-header',
        })
        groupItems.forEach(item => {
            options.push({
                key: String(item.opt?.ResponseId || item.opt?.Id || item.value),
                value: item.value,
                /* Short label for closed display + open search box (like Mat Type) */
                text: item.locationName || item.value,
                meta: item,
            })
        })
    })
    return options
}

/** Filter grouped supplier options while keeping section headers. */
export const filterSupplierDropdownOptions = (options, query) => {
    const q = String(query || '').trim().toLowerCase()
    if (!q) return options

    const itemMatches = opt => {
        if (opt?.disabled || opt?.className === 'supplier-group-header') {
            return false
        }
        const locationName = String(opt?.meta?.locationName || '')
            .trim()
            .toLowerCase()
        const label = String(opt?.text || opt?.value || '')
            .trim()
            .toLowerCase()
        return (
            (locationName && locationName.includes(q)) ||
            (label && label.includes(q))
        )
    }

    const filtered = []
    let pendingHeader = null

    for (let i = 0; i < (options || []).length; i += 1) {
        const opt = options[i]
        if (opt.disabled || opt.className === 'supplier-group-header') {
            pendingHeader = opt
        } else if (itemMatches(opt)) {
            if (pendingHeader) {
                filtered.push(pendingHeader)
                pendingHeader = null
            }
            filtered.push(opt)
        }
    }

    return filtered
}

const clientTypeNameFromCode = clientType => {
    const code = String(clientType || '').trim().toUpperCase()
    if (code === 'C') return 'Cintas'
    if (code === 'I') return 'Guardian International'
    if (code === 'L') return 'Guardian'
    return ''
}

/** Build grouped customer dropdown options from GET /Customer/Extensive (All Claims filter). */
export const buildCustomerExtensiveDropdownOptions = (rows = []) => {
    const items = (rows || []).map(row => {
        const clientType = String(row?.ClientType || '').trim().toUpperCase()
        const locationName = String(row?.LocationName || '').trim()
        const locNum = String(row?.LocNum || '').trim()
        const clientTypeName = clientTypeNameFromCode(clientType)
        const uniqueId = row?.UniqueId
        return {
            locationName,
            locNum,
            clientType,
            clientTypeName,
            value: uniqueId,
            text: [locNum, locationName].filter(Boolean).join(' '),
        }
    })

    const groups = new Map()
    items.forEach(item => {
        const badge = getSupplierBadgeFromClientType(
            item.clientTypeName,
            item.clientType,
        )
        const groupKey = badge.variant
        if (!groups.has(groupKey)) {
            groups.set(groupKey, { badge, items: [] })
        }
        groups.get(groupKey).items.push(item)
    })

    const options = []
    const groupEntries = [...groups.entries()]
    groupEntries
        .sort(
            (a, b) =>
                (SUPPLIER_GROUP_ORDER[a[1].badge.variant] ?? 99) -
                (SUPPLIER_GROUP_ORDER[b[1].badge.variant] ?? 99),
        )
        .forEach(([, group]) => {
            const count = group.items.length
            options.push({
                key: `__header_${group.badge.variant}`,
                value: `__header_${group.badge.variant}`,
                text: getSupplierGroupHeaderLabel(group.badge.variant, count),
                disabled: true,
                className: 'supplier-group-header',
            })
            group.items.forEach(item => {
                options.push({
                    key: String(item.value),
                    value: item.value,
                    text: item.text,
                    meta: {
                        locationName: item.locationName,
                        locNum: item.locNum,
                        clientType: item.clientType,
                        clientTypeName: item.clientTypeName,
                        uniqueId: item.value,
                    },
                })
            })
        })
    return options
}

export const parseClaimListResponse = response => {
    if (Array.isArray(response)) {
        return { items: response, totalCount: response.length }
    }
    const items =
        response?.Items ||
        response?.items ||
        response?.rows ||
        response?.Data ||
        []
    const totalCount =
        response?.TotalCount ??
        response?.totalCount ??
        response?.MetaData?.Total ??
        (Array.isArray(items) ? items.length : 0)
    return {
        items: Array.isArray(items) ? items : [],
        totalCount: Number(totalCount) || 0,
    }
}

export const getClaimRowId = item => {
    const raw = item?.AuditId ?? item?.ClaimNumber
    if (raw == null || raw === '') return ''
    return String(raw)
}

export const getClaimAuditId = item => {
    const auditId = Number(item?.AuditId)
    return Number.isFinite(auditId) && auditId > 0 ? auditId : null
}

export const getClaimRmaDisplayValues = item => {
    const value = String(item?.RAMNumber || '').trim()
    return value ? [value] : []
}

export const getClaimNonRmaNumber = item =>
    String(item?.NonRMANumber || item?.NonRmaNumber || '').trim()

/** Claim/List field for the All Claims "SFO/Order #" column. */
export const getClaimSfoOrderNumber = item =>
    String(item?.SFOOrder || item?.SfoOrderNumber || '').trim()

export const getClaimDisplayName = item => {
    const claimNumber = String(item?.ClaimNumber || '').trim()
    if (claimNumber) return claimNumber
    const customer = String(item?.CustomerName || '').trim()
    if (customer) return `(pending) — ${customer}`
    return '(pending)'
}

export const findSupplierOptionByValue = (options, value) => {
    const normalized = String(value || '').trim().toLowerCase()
    if (!normalized) return null
    return (options || []).find(opt => {
        if (opt?.disabled || String(opt?.value || '').startsWith('__header_')) {
            return false
        }
        const val = String(opt?.value || '').trim().toLowerCase()
        const loc = String(opt?.meta?.locationName || '')
            .trim()
            .toLowerCase()
        return val === normalized || loc === normalized
    })
}

export const extractSupplierOptionsFromAudit = (sections, templateData) => {
    const portal = getPortalTemplateSection(sections)
    if (!portal) return []
    const map = buildQuestionMapByFaq(portal.Questions)
    const customerQ = findQuestionByAliases(map, PORTAL_FAQ.customer)
    return buildSupplierDropdownOptionsFromQuestion(customerQ, templateData)
}

/** Split supplier dropdown text for badge + summary panel (Cintas mockup). */
export const parseSupplierDisplay = displayText => {
    const text = String(displayText || '').trim()
    if (!text) {
        return { badge: null, title: '', subtitle: '' }
    }
    const hasCintas =
        /^cintas\b/i.test(text) || /\bcintas\b/i.test(text)
    const badge = hasCintas ? 'CINTAS' : null
    const body = text.replace(/^cintas\s*/i, '').trim()

    const commaParts = body.split(',').map(s => s.trim()).filter(Boolean)
    if (commaParts.length >= 3) {
        return {
            badge,
            title: commaParts[0],
            subtitle: commaParts.slice(1).join(', '),
        }
    }
    if (commaParts.length === 2) {
        return {
            badge,
            title: commaParts[0],
            subtitle: commaParts[1],
        }
    }

    const streetMatch = body.match(
        /^(.+?)\s+(\d+[\w\s.,-]+(?:,\s*[A-Z]{2})?(?:\s+\d{5})?)$/i,
    )
    if (streetMatch) {
        return {
            badge,
            title: streetMatch[1].trim(),
            subtitle: streetMatch[2].trim(),
        }
    }

    return { badge, title: body, subtitle: '' }
}

const parseDateValue = raw => {
    if (!raw) return null
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? null : d
}

const formatDateForApi = value => {
    const d = parseDateValue(value)
    if (!d) return ''
    const pad = n => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export const extractClaimPortalRowFromSection = section => {
    const map = buildQuestionMapByFaq(section?.Questions)
    const customerQ = findQuestionByAliases(map, PORTAL_FAQ.customer)
    const matTypeQ = findQuestionByAliases(map, PORTAL_FAQ.matType)
    const itemSketchQ = findQuestionByAliases(map, PORTAL_FAQ.itemSketch)
    const sizeQ = findQuestionByAliases(map, PORTAL_FAQ.size)
    const quantityQ = findQuestionByAliases(map, PORTAL_FAQ.quantity)
    const sfoQ = findQuestionByAliases(map, PORTAL_FAQ.sfoOrder)
    const detailsQ = findQuestionByAliases(map, PORTAL_FAQ.complaintDetails)
    const codeQ = findQuestionByAliases(map, PORTAL_FAQ.code)
    const rmaQ = findQuestionByAliases(map, PORTAL_FAQ.rma)
    const creditQ = findQuestionByAliases(map, PORTAL_FAQ.creditIssue)
    const notesQ = findQuestionByAliases(map, PORTAL_FAQ.notes)

    const supplier = customerQ
        ? getSelectedMultipleText(customerQ) || getSingleResponseText(customerQ)
        : ''

    return {
        sectionId: section?.SectionId,
        sectionRef: section,
        supplier,
        matType: matTypeQ
            ? getSelectedMultipleText(matTypeQ) || getSingleResponseText(matTypeQ)
            : '',
        itemSketchNumber: itemSketchQ ? getSingleResponseText(itemSketchQ) : '',
        size: sizeQ
            ? getSelectedMultipleText(sizeQ) || getSingleResponseText(sizeQ)
            : '',
        quantity: quantityQ ? getSingleResponseText(quantityQ) : '',
        sfoOrder: sfoQ ? getSingleResponseText(sfoQ) : '',
        complaintDetails: detailsQ ? getSingleResponseText(detailsQ) : '',
        code: codeQ
            ? getSelectedMultipleText(codeQ) || getSingleResponseText(codeQ)
            : '',
        rma: rmaQ ? getSingleResponseText(rmaQ) : '',
        creditIssue: creditQ
            ? getSelectedMultipleText(creditQ) || getSingleResponseText(creditQ)
            : '',
        notes: notesQ ? getSingleResponseText(notesQ) : '',
        itemSketchQuestionId: itemSketchQ?.Id,
        complaintDetailsQuestionId: detailsQ?.Id,
    }
}

export const extractQualityFormFromSection = section => {
    if (!section) {
        return {
            receivingDate: null,
            carrier: '',
            tracking: '',
            emboss: '',
            packingMatCondition: '',
            disposition: '',
            status: '',
            notes: '',
        }
    }
    const map = buildQuestionMapByFaq(section?.Questions)
    const receivingQ = findQuestionByAliases(map, QUALITY_FAQ.receivingDate)
    const carrierQ = findQuestionByAliases(map, QUALITY_FAQ.carrier)
    const trackingQ = findQuestionByAliases(map, QUALITY_FAQ.tracking)
    const embossQ = findQuestionByAliases(map, QUALITY_FAQ.emboss)
    const packingQ = findQuestionByAliases(map, QUALITY_FAQ.packingMatCondition)
    const dispositionQ = findQuestionByAliases(map, QUALITY_FAQ.disposition)
    const statusQ = findQuestionByAliases(map, QUALITY_FAQ.status)
    const notesQ = findQuestionByAliases(map, QUALITY_FAQ.notes)

    const receivingRaw = receivingQ ? getSingleResponseText(receivingQ) : ''

    return {
        receivingDate: parseDateValue(receivingRaw),
        carrier: carrierQ
            ? getSelectedMultipleText(carrierQ) || getSingleResponseText(carrierQ)
            : '',
        tracking: trackingQ ? getSingleResponseText(trackingQ) : '',
        emboss: embossQ ? getSingleResponseText(embossQ) : '',
        packingMatCondition: packingQ
            ? getSelectedMultipleText(packingQ) ||
              getSingleResponseText(packingQ)
            : '',
        disposition: dispositionQ
            ? getSelectedMultipleText(dispositionQ) ||
              getSingleResponseText(dispositionQ)
            : '',
        status: statusQ
            ? getSelectedMultipleText(statusQ) || getSingleResponseText(statusQ)
            : '',
        notes: notesQ ? getSingleResponseText(notesQ) : '',
    }
}

export const extractCompletionFormFromSection = section => {
    if (!section) {
        return { creditIssue: '', notes: '' }
    }
    const map = buildQuestionMapByFaq(section?.Questions)
    const creditQ = findQuestionByAliases(map, COMPLETION_FAQ.creditIssue)
    const notesQ = findQuestionByAliases(map, COMPLETION_FAQ.notes)
    return {
        creditIssue: creditQ
            ? getSelectedMultipleText(creditQ) || getSingleResponseText(creditQ)
            : '',
        notes: notesQ ? getSingleResponseText(notesQ) : '',
    }
}

/** @deprecated Use isClaimPortalRowFilled — kept for standalone log screen. */
export const isClaimRowFilled = row => isClaimPortalRowFilled(row)

export const isClaimPortalRowFilled = row =>
    !!(
        row &&
        (String(row.supplier || '').trim() ||
            String(row.matType || '').trim() ||
            String(row.itemSketchNumber || '').trim() ||
            String(row.complaintDetails || '').trim() ||
            String(row.quantity || '').trim())
    )

export const buildClaimRowsFromAuditSections = sections => {
    const portalSections = listClaimPortalSectionsFromAudit(sections)
    if (!portalSections.length) {
        return [
            {
                sectionId: null,
                supplier: '',
                matType: '',
                itemSketchNumber: '',
                size: '',
                quantity: '',
                sfoOrder: '',
                complaintDetails: '',
                code: '',
                rma: '',
                creditIssue: '',
                notes: '',
            },
        ]
    }

    const rows = portalSections.map(section =>
        extractClaimPortalRowFromSection(section),
    )

    if (!rows.some(r => !isClaimPortalRowFilled(r))) {
        rows.push({
            sectionId: null,
            supplier: '',
            matType: '',
            itemSketchNumber: '',
            size: '',
            quantity: '',
            sfoOrder: '',
            complaintDetails: '',
            code: '',
            rma: '',
            creditIssue: '',
            notes: '',
        })
    }

    return rows
}

const generateClaimCopySectionId = () =>
    `0.${Math.random().toString().slice(2)}${Date.now().toString().slice(-6)}`

const zeroQuestionIdsForCopiedSection = question => {
    if (!question) return question
    const next = { ...question, Id: 0 }
    if (next.Answers?.MultipleResponse) {
        next.Answers = {
            ...next.Answers,
            MultipleResponse: next.Answers.MultipleResponse.map(opt => ({
                ...opt,
                Id: 0,
            })),
        }
    }
    return next
}

const stripCopiedAnswerInstanceIds = question => {
    if (!question?.Answers) return question
    const next = { ...question, Answers: { ...question.Answers } }
    if (next.Answers.SingleResponse) {
        next.Answers.SingleResponse = {
            ...next.Answers.SingleResponse,
            Id: 0,
        }
    }
    if (Array.isArray(next.Answers.MultipleResponse)) {
        next.Answers.MultipleResponse = next.Answers.MultipleResponse.map(opt => ({
            ...opt,
            Id: 0,
        }))
    }
    return next
}

const mergePortalRowIntoSectionQuestions = (section, row) =>
    mergeSectionQuestionsByBindings(section, [
        { aliases: PORTAL_FAQ.customer, value: row?.supplier },
        { aliases: PORTAL_FAQ.matType, value: row?.matType },
        { aliases: PORTAL_FAQ.itemSketch, value: row?.itemSketchNumber },
        { aliases: PORTAL_FAQ.size, value: row?.size },
        { aliases: PORTAL_FAQ.quantity, value: row?.quantity },
        { aliases: PORTAL_FAQ.sfoOrder, value: row?.sfoOrder },
        { aliases: PORTAL_FAQ.complaintDetails, value: row?.complaintDetails },
        { aliases: PORTAL_FAQ.code, value: row?.code },
        { aliases: PORTAL_FAQ.rma, value: row?.rma },
        { aliases: PORTAL_FAQ.creditIssue, value: row?.creditIssue },
        { aliases: PORTAL_FAQ.notes, value: row?.notes },
    ])

const mergeQualityIntoSection = (section, quality) => {
    if (!section || !quality) return section
    return mergeSectionQuestionsByBindings(section, [
        {
            aliases: QUALITY_FAQ.receivingDate,
            value: quality.receivingDate,
            isDate: true,
        },
        { aliases: QUALITY_FAQ.carrier, value: quality.carrier },
        { aliases: QUALITY_FAQ.tracking, value: quality.tracking },
        { aliases: QUALITY_FAQ.emboss, value: quality.emboss },
        {
            aliases: QUALITY_FAQ.packingMatCondition,
            value: quality.packingMatCondition,
        },
        { aliases: QUALITY_FAQ.disposition, value: quality.disposition },
        { aliases: QUALITY_FAQ.status, value: quality.status },
        { aliases: QUALITY_FAQ.notes, value: quality.notes },
    ])
}

const mergeCompletionIntoSection = (section, completion) => {
    if (!section || !completion) return section
    return mergeSectionQuestionsByBindings(section, [
        { aliases: COMPLETION_FAQ.creditIssue, value: completion.creditIssue },
        { aliases: COMPLETION_FAQ.notes, value: completion.notes },
    ])
}

export const buildCustomerClaimPayload = (
    auditData,
    claimRows,
    qualityForm,
    completionForm,
    extraAnswers = null,
) => {
    if (!auditData?.Sections?.length) return auditData

    const portalSections = listClaimPortalSectionsFromAudit(auditData.Sections)
    if (!portalSections.length) return auditData

    const templateSection = JSON.parse(JSON.stringify(portalSections[0]))
    const filledRows = (claimRows || []).filter(isClaimPortalRowFilled)

    const rowsForPayload =
        filledRows.length > 0
            ? filledRows
            : [
                  claimRows?.[0] || {
                      supplier: '',
                      matType: '',
                      itemSketchNumber: '',
                      complaintDetails: '',
                  },
              ]

    const buildSyntheticSectionForRow = (row, rowIndex) => {
        const isCopy = rowIndex > 0
        const clone = JSON.parse(JSON.stringify(templateSection))
        const sectionDisplayName = String(
            templateSection.SectionName || PORTAL_SECTION_NAME,
        ).trim()

        if (isCopy) {
            clone.SectionId = generateClaimCopySectionId()
            clone.IsCopiedSection = true
            if (clone.Id !== undefined && clone.Id !== null) {
                clone.Id = 0
            }
        }

        clone.SectionName = sectionDisplayName
        clone.Questions = (clone.Questions || []).map(q => {
            let next = zeroQuestionIdsForCopiedSection(q)
            if (isCopy) {
                next = stripCopiedAnswerInstanceIds(next)
            }
            next.SectionId = clone.SectionId
            next.SectionName = sectionDisplayName
            next.IsCopiedSection = isCopy
            return next
        })

        return mergePortalRowIntoSectionQuestions(clone, row)
    }

    const portalIndexBySectionId = new Map(
        portalSections.map((s, idx) => [String(s.SectionId), idx]),
    )

    const nextSections = []
    const syntheticExtras = []

    const auditSections = auditData.Sections || []
    auditSections.forEach(section => {
        const sid = String(section.SectionId)
        const portalIdx = portalIndexBySectionId.get(sid)

        if (portalIdx != null) {
            if (portalIdx < rowsForPayload.length) {
                const row =
                    rowsForPayload[portalIdx] ||
                    rowsForPayload[rowsForPayload.length - 1]
                nextSections.push(
                    mergePortalRowIntoSectionQuestions(
                        JSON.parse(JSON.stringify(section)),
                        row,
                    ),
                )
            } else {
                nextSections.push(section)
            }
            return
        }

        if (normalizeFaq(section?.SectionName) === normalizeFaq(QUALITY_SECTION_NAME)) {
            nextSections.push(mergeQualityIntoSection(section, qualityForm))
            return
        }
        if (
            normalizeFaq(section?.SectionName) ===
            normalizeFaq(COMPLETION_SECTION_NAME)
        ) {
            nextSections.push(mergeCompletionIntoSection(section, completionForm))
            return
        }

        nextSections.push(section)
    })

    for (let i = portalSections.length; i < rowsForPayload.length; i += 1) {
        syntheticExtras.push(
            buildSyntheticSectionForRow(rowsForPayload[i], i),
        )
    }

    return {
        ...auditData,
        Sections: [...nextSections, ...syntheticExtras].map(section =>
            applyExtraAnswersToSectionQuestions(section, extraAnswers),
        ),
    }
}

export async function ensureCustomerClaimServerSectionsLoaded({
    audit,
    claimRows,
    routeAuditId,
    auditNumericId,
}) {
    const filledRows = (claimRows || []).filter(isClaimPortalRowFilled)
    const targetCount = Math.max(filledRows.length, 1)
    if (!audit?.Sections?.length || !auditNumericId || !routeAuditId || targetCount <= 1) {
        return audit
    }

    const countPortalSections = a =>
        listClaimPortalSectionsFromAudit(a?.Sections || []).length

    let current = audit
    const portalSections = listClaimPortalSectionsFromAudit(current.Sections || [])
    const primary = portalSections[0]
    if (!primary?.SectionId) return current

    /* eslint-disable no-await-in-loop */
    while (countPortalSections(current) < targetCount) {
        await InspectionService.copySection({
            AuditUniqueId: auditNumericId,
            SectionId: primary.SectionId,
            CopyAuditId: 0,
            SectionName: String(primary.SectionName || PORTAL_SECTION_NAME).trim(),
        })
        let fresh = null
        try {
            fresh = await InspectionService.getAudit(String(routeAuditId))
        } catch (e) {
            console.error('[CustomerClaim] getAudit after copySection failed:', e)
            break
        }
        current = fresh || current
    }
    /* eslint-enable no-await-in-loop */

    return current
}

export const categorizeClaimRow = row => {
    const supplier = String(row?.supplier || '').trim().toLowerCase()
    const matType = String(row?.matType || '').trim().toLowerCase()
    if (supplier === 'cintas') return 'cintas'
    if (matType === 'international') return 'international'
    return 'our'
}

export const resolveActiveClaimPortalRow = (rows, activeSectionId) => {
    const list = rows || []
    const filled = list.filter(isClaimPortalRowFilled)
    if (!filled.length) {
        return list[0] || null
    }
    if (activeSectionId) {
        const match = filled.find(
            row => String(row.sectionId) === String(activeSectionId),
        )
        if (match) return match
    }
    return filled[filled.length - 1]
}

export const mergeClaimRowsWithPortalDraft = (
    claimRows,
    supplierSelected,
    portalDraft,
    activeSectionId = null,
) => {
    const filled = (claimRows || []).filter(isClaimPortalRowFilled)
    const activeRow = resolveActiveClaimPortalRow(claimRows, activeSectionId)
    const draftRow = {
        sectionId: activeRow?.sectionId ?? filled[0]?.sectionId ?? null,
        supplier: supplierSelected || '',
        ...(portalDraft || {}),
    }
    if (!isClaimPortalRowFilled(draftRow)) {
        return filled
    }
    if (!filled.length) {
        return [draftRow]
    }
    const targetIdx = activeSectionId
        ? filled.findIndex(
              row => String(row.sectionId) === String(activeSectionId),
          )
        : filled.length - 1
    const mergeIdx = targetIdx >= 0 ? targetIdx : filled.length - 1
    const next = filled.map((row, idx) =>
        idx === mergeIdx
            ? { ...row, ...draftRow, sectionId: row.sectionId }
            : row,
    )
    const draftMatchesExisting = filled.some(
        row =>
            String(row.supplier || '').trim() ===
                String(supplierSelected || '').trim() &&
            String(row.sectionId || '') === String(draftRow.sectionId || ''),
    )
    if (!draftMatchesExisting && filled.length > 0) {
        const alreadyInList = filled.some(
            row =>
                String(row.matType || '') === String(draftRow.matType || '') &&
                String(row.itemSketchNumber || '') ===
                    String(draftRow.itemSketchNumber || '') &&
                String(row.complaintDetails || '') ===
                    String(draftRow.complaintDetails || ''),
        )
        if (!alreadyInList) {
            next.push(draftRow)
        }
    }
    return next
}

export const buildCustomerClaimCompletionErrors = (
    claimRows,
    supplierSelected,
    portalDraft,
) => {
    const errors = {}
    const filled = mergeClaimRowsWithPortalDraft(
        claimRows,
        supplierSelected,
        portalDraft,
        null,
    ).filter(isClaimPortalRowFilled)

    if (!supplierSelected) {
        errors.__customer_claim_supplier__ = {
            message: 'Please select a supplier.',
            type: 'error',
        }
    }

    if (!filled.length) {
        errors.__customer_claim_entries__ = {
            message: 'Please log at least one complaint before completing.',
            type: 'error',
        }
        return errors
    }

    filled.forEach((row, idx) => {
        const label = row.supplier || `Complaint ${idx + 1}`
        if (!String(row.supplier || '').trim()) {
            errors[`__customer_claim_row_${idx}_supplier__`] = {
                message: `${label}: Supplier is required.`,
                type: 'error',
            }
        }
    })

    return errors
}

export const formatInspectionQuestionAnswerText = question => {
    if (!question) return ''
    const multi = question?.Answers?.MultipleResponse || []
    if (multi.length) {
        return getSelectedMultipleText(question) || ''
    }
    return getSingleResponseText(question)
}

export const buildSectionQuestionEditChanges = (beforeSection, afterSection) => {
    const beforeQuestions = beforeSection?.Questions || []
    const afterById = new Map(
        (afterSection?.Questions || []).map(question => [
            String(question?.Id),
            question,
        ]),
    )
    const changes = []

    beforeQuestions.forEach(beforeQuestion => {
        const questionId = String(beforeQuestion?.Id || '')
        if (!questionId) return

        const afterQuestion = afterById.get(questionId)
        if (!afterQuestion) return

        const oldValue = formatInspectionQuestionAnswerText(beforeQuestion)
        const newValue = formatInspectionQuestionAnswerText(afterQuestion)
        if (oldValue === newValue) return

        changes.push({
            QuestionId: beforeQuestion.Id,
            FieldName: String(
                beforeQuestion?.FAQ ||
                    beforeQuestion?.Question ||
                    beforeQuestion?.DisplayText ||
                    `Question ${questionId}`,
            ).trim(),
            OldValue: oldValue,
            NewValue: newValue,
        })
    })

    return changes
}

export const findAuditSectionById = (auditData, sectionId) => {
    if (!auditData?.Sections?.length || sectionId == null) return null
    const target = String(sectionId).trim()
    return (
        auditData.Sections.find(
            section => String(section?.SectionId).trim() === target,
        ) || null
    )
}
